import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icon, Tooltip } from '~/components';
import { Button } from '~/components/Button';
import type { DropdownOption } from '~/components/Dropdown/types/DropdownOption';
import { TERRITORIES } from '~/lib/territories/territories';
import {
  MEDIA_PLAN_GOAL,
  PLATFORM_TABLE_GRID_COLUMNS,
} from '~/modules/artist-roster/pages/artist-details/pages/media-planner/constants';
import type { MediaPlanForm } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/types';

import { Platform } from './Platform';

export function PlatformsTab() {
  const { t } = useTranslation('campaign-planner');
  const { t: tTerritories } = useTranslation('territories');

  // Market options from territories
  const MARKET_OPTIONS: DropdownOption[] = Object.values(TERRITORIES).map(
    (territory) => ({
      label: tTerritories(`names.${territory.code}`),
      value: territory.code,
    }),
  );

  // Goal options
  const GOAL_OPTIONS: DropdownOption[] = MEDIA_PLAN_GOAL.map((goal) => {
    return {
      label: t(`digitalCampaigns.mediaPlan.platforms.goals.${goal}`),
      value: goal,
    };
  });

  const { control } = useFormContext<MediaPlanForm>();

  const { fields } = useFieldArray({
    control,
    name: 'platforms',
  });

  return (
    <div role="tabpanel" data-testid="platforms">
      {fields.length ? (
        <>
          <div className="flex justify-end gap-lg">
            <Button
              type="button"
              variant="text"
              size="small"
              className="font-semibold"
              disabled
            >
              {t('digitalCampaigns.mediaPlan.platforms.buttons.resetPlan')}
            </Button>
            {/* @TODO WMG1-612 Export CSV Button */}
            <Button type="button" variant="primary" size="small">
              {t('digitalCampaigns.mediaPlan.platforms.buttons.export')}
            </Button>
          </div>
          <div
            className="mb-sm grid items-end gap-sm border-b px-sm pb-sm text-caption-md font-bold leading-tight border-emphasis-subtle text-subtle"
            style={{ gridTemplateColumns: PLATFORM_TABLE_GRID_COLUMNS }}
          >
            <div>{t('digitalCampaigns.mediaPlan.platforms.budget')}</div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.market.label')}</div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.goals.label')}</div>
            <div>
              {t('digitalCampaigns.mediaPlan.platforms.customAudienceSize')}
              <Tooltip
                aria-label={t(
                  'digitalCampaigns.mediaPlan.platforms.customAudienceSizeTooltip',
                )}
                size="sm"
                contentClassName="font-semibold"
                content={t(
                  'digitalCampaigns.mediaPlan.platforms.customAudienceSizeTooltip',
                )}
                triggerAsChild={true}
              >
                <span className="ml-xs inline-flex cursor-pointer">
                  <Icon type="circle-info" variant="light" size="xs" />
                </span>
              </Tooltip>
            </div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.frequency')}</div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.results')}</div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.costPerResult')}</div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.resultRate')}</div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.impressions')}</div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.reach')}</div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.videoViews')}</div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.clicks')}</div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.engagement')}</div>
            <div>{t('digitalCampaigns.mediaPlan.platforms.actions')}</div>
          </div>
        </>
      ) : null}
      {fields.map((field, index) => (
        <Platform
          onRemove={() => {}}
          key={field.id}
          platformIndex={index}
          platform={field.mediaPlatform}
          marketOptions={MARKET_OPTIONS}
          goalOptions={GOAL_OPTIONS}
        />
      ))}
      <div className="mt-md pb-px pl-px">
        <Button type="button" variant="secondary" size="small">
          <span>{t('digitalCampaigns.mediaPlan.platforms.addPlatform')}</span>
          <Icon
            type="chevron-down"
            variant="solid"
            size="sm"
            className="-mr-2xs ml-sm"
          />
        </Button>
      </div>
    </div>
  );
}

PlatformsTab.displayName = 'PlatformsTab';
